import { useEffect, useMemo, useState } from 'react';
import { Alert, Col, Container, Row, Spinner } from 'react-bootstrap';
import { bookingApi } from '../../services/booking.api';
import { normalizeSupplierBookingDetail } from '../../utils/bookingNormalizer';
import { useParams } from "react-router-dom";
import SupplierBookingSummary from './SupplierBookingSummary';
import SupplierCustomerCard from './SupplierCustomerCard';
import SupplierPricingCard from './SupplierPricingCard';
import SupplierActionsCard from './SupplierActionsCard';
import SupplierRentalPolicyCard from './SupplierRentalPolicyCard';

const SupplierDecisionPage = () => {
    const { id } = useParams()
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [rawData, setRawData] = useState(null);
    const [error, setError] = useState('');
    const [actionMessage, setActionMessage] = useState('');

    const booking = useMemo(() => {
        return rawData ? normalizeSupplierBookingDetail(rawData) : null;
    }, [rawData]);

    const loadBooking = async () => {
        try {
            setLoading(true);
            setError('');
            const res = await bookingApi.getById(id);
            setRawData(res);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load booking.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) loadBooking();
    }, [id]);

    const handleConfirm = async () => {
        try {
            setSubmitting(true);
            setError('');
            setActionMessage('');

            await bookingApi.confirmBySupplier(id);

            setActionMessage('Booking confirmed successfully.');
            await loadBooking();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to confirm booking.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = async (payload) => {
        try {
            setSubmitting(true);
            setError('');
            setActionMessage('');

            await bookingApi.cancelBySupplier(id, payload);

            setActionMessage('Cancellation note saved successfully.');
            await loadBooking();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to save cancellation note.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAlternative = async (payload) => {
        try {
            setSubmitting(true);
            setError('');
            setActionMessage('');

            await bookingApi.proposeAlternativeBySupplier(id, payload);

            setActionMessage('Alternative proposal sent to customer.');
            await loadBooking();
        } catch (err) {
            setError(
                err?.response?.data?.message || 'Failed to send alternative proposal.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner />
            </div>
        );
    }

    if (!booking) {
        return <Alert variant="warning">Booking not found.</Alert>;
    }

    return (
        <Container fluid className="py-4">
            {error ? <Alert variant="danger">{error}</Alert> : null}
            {actionMessage ? <Alert variant="success">{actionMessage}</Alert> : null}

            <Row className="g-4">
                <Col lg={8}>
                    <SupplierBookingSummary booking={booking} />
                    <SupplierRentalPolicyCard booking={booking} />
                    <SupplierCustomerCard booking={booking} />
                </Col>

                <Col lg={4}>
                    <SupplierActionsCard
                        booking={booking}
                        submitting={submitting}
                        onConfirm={handleConfirm}
                        onCancel={handleCancel}
                        onAlternative={handleAlternative}
                    />

                    <div className="mt-4">
                        <SupplierPricingCard booking={booking} />
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default SupplierDecisionPage;